import React, { useState } from 'react';
import {
  Dialog, DialogTitle, DialogContent, DialogActions,
  Grid, TextField, Button, Typography, MenuItem,
  Switch, FormControlLabel, Divider, Collapse, Box,
  Alert, InputAdornment, IconButton, Tooltip,
  useMediaQuery, useTheme
} from '@mui/material';
import { ExpandMore, ExpandLess, Visibility, VisibilityOff, InfoOutlined as InfoIcon } from '@mui/icons-material';
import PasswordStrengthIndicator from '../PasswordStrengthIndicator';
import { grantableRoleOptions, isBuiltinAdmin, isOwnLogin } from '../../utils/roleOptions';
import { exemptSalaryFloor, dailyOvertimeRule } from '../../utils/stateOvertimeRules';
import { resolveW4Floor } from '../../utils/w4Floor';

const US_STATES = [
  { code: '', label: 'None' },
  { code: 'AL', label: 'Alabama' }, { code: 'AK', label: 'Alaska' }, { code: 'AZ', label: 'Arizona' },
  { code: 'AR', label: 'Arkansas' }, { code: 'CA', label: 'California' }, { code: 'CO', label: 'Colorado' },
  { code: 'CT', label: 'Connecticut' }, { code: 'DE', label: 'Delaware' }, { code: 'FL', label: 'Florida' },
  { code: 'GA', label: 'Georgia' }, { code: 'HI', label: 'Hawaii' }, { code: 'ID', label: 'Idaho' },
  { code: 'IL', label: 'Illinois' }, { code: 'IN', label: 'Indiana' }, { code: 'IA', label: 'Iowa' },
  { code: 'KS', label: 'Kansas' }, { code: 'KY', label: 'Kentucky' }, { code: 'LA', label: 'Louisiana' },
  { code: 'ME', label: 'Maine' }, { code: 'MD', label: 'Maryland' }, { code: 'MA', label: 'Massachusetts' },
  { code: 'MI', label: 'Michigan' }, { code: 'MN', label: 'Minnesota' }, { code: 'MS', label: 'Mississippi' },
  { code: 'MO', label: 'Missouri' }, { code: 'MT', label: 'Montana' }, { code: 'NE', label: 'Nebraska' },
  { code: 'NV', label: 'Nevada' }, { code: 'NH', label: 'New Hampshire' }, { code: 'NJ', label: 'New Jersey' },
  { code: 'NM', label: 'New Mexico' }, { code: 'NY', label: 'New York' }, { code: 'NC', label: 'North Carolina' },
  { code: 'ND', label: 'North Dakota' }, { code: 'OH', label: 'Ohio' }, { code: 'OK', label: 'Oklahoma' },
  { code: 'OR', label: 'Oregon' }, { code: 'PA', label: 'Pennsylvania' }, { code: 'RI', label: 'Rhode Island' },
  { code: 'SC', label: 'South Carolina' }, { code: 'SD', label: 'South Dakota' }, { code: 'TN', label: 'Tennessee' },
  { code: 'TX', label: 'Texas' }, { code: 'UT', label: 'Utah' }, { code: 'VT', label: 'Vermont' },
  { code: 'VA', label: 'Virginia' }, { code: 'WA', label: 'Washington' }, { code: 'WV', label: 'West Virginia' },
  { code: 'WI', label: 'Wisconsin' }, { code: 'WY', label: 'Wyoming' }, { code: 'DC', label: 'District of Columbia' },
];

// Mirror of server/services/overtimePolicyService.js OVERTIME_EXCEPTIONS (the server
// and the DB CHECK are the source of truth; keep the codes identical).
const OVERTIME_EXCEPTIONS = [
  { code: '7k_fire_police', label: 'FLSA § 7(k) — public agencies only (29 CFR § 553.201): fire protection / law enforcement work period' },
  { code: '7_other_partial', label: 'Other FLSA § 7 partial overtime exemption — cite the subsection (e.g. § 7(b)(1)/(2), § 7(m)). Daily limits are not calculated. For a full § 13 exemption, turn overtime off instead.' },
];
// hr-reference/federal-flsa.json (overtime.threshold_hours) and
// hr-reference/federal-exempt-thresholds.json (federal_salary_threshold.weekly/.annual,
// duties_tests Computer Employee salary_options.hourly). Not importable at runtime.
const FLSA_WEEKLY_OVERTIME_THRESHOLD = 40;
const FLSA_EXEMPT_WEEKLY_SALARY = 684;
const FLSA_EXEMPT_ANNUAL_SALARY = 35568;
const FLSA_COMPUTER_HOURLY = 27.63;
// hr-reference/federal-flsa.json overtime.rate_multiplier; server overtimePolicyService is the source of truth
const FLSA_OVERTIME_MULTIPLIER = 1.5;
const OVERTIME_SWITCH_TIP = 'Non-exempt employees are owed at least 1.5× pay after 40 hours in a workweek (FLSA, 29 U.S.C. § 207(a)(1)). Turn this off only for someone who is exempt (a salary of at least $684/week federally, more in some states, plus a duties test) or otherwise outside FLSA overtime. Changing it needs the Manage employees or Run payroll permission and is written to the audit log.';
const OVERTIME_MULTIPLIER_TIP = 'Overtime hours are paid at this multiple of the regular rate. The FLSA minimum is 1.5× (29 U.S.C. § 207(a)(1)), so a lower value is refused while overtime is on, and anything under 1× or over 3× is refused at all. A higher value (for example 2× under a union contract) is a premium and is noted in the audit log. In Edit only an Admin can change it, and it needs the View pay rates permission.';
const RESIDENT_PSD_TIP = "The 6-digit Pennsylvania PSD code of the municipality and school district where this person lives, from their Residency Certification Form. Look it up on the DCED Municipal Statistics site (PSD Codes and EIT Rates). Local Earned Income Tax returns list it for each employee.";
const WORK_PSD_TIP = "The 6-digit PSD code of the place where this person works. For someone who works from home all or most of the time, enter the home PSD code. If they split time between an office and home, ask the tax collector which worksite to report. The EIT and LST withheld are sent to the tax collector for this location.";
const LOCAL_COLLECTOR_TIP = "The tax collector for the work location, the one you send this person's withheld EIT and LST to (for example Berkheimer Tax Administrator for Millcreek Township, Erie County). Type it the same way for everyone who uses that collector: HourTrax groups local tax figures by this name.";
const WORK_STATE_TIP = 'The state where this person actually works. State overtime law and exempt salary floors follow the work location, not the home address. Same as tax State uses the State from Tax / W-4. HourTrax uses it only for the warnings in this section; overtime is still calculated per workweek. Changing it needs the Manage employees or Run payroll permission.';
const OVERTIME_THRESHOLD_TIP = 'Hours in a workweek before overtime starts. The FLSA number is 40; a lower threshold is allowed (more generous). A higher one is refused unless you record an FLSA Exception and an Exception Reason, which are saved on this person and written to the audit log.';
const OVERTIME_EXCEPTION_TIP = 'The statutory basis for overtime starting after more than 40 hours in a week. HourTrax still applies the threshold per workweek, so enter the weekly equivalent of the work period the exception allows (for example § 7(k): about 53 h fire / 43 h law enforcement). The § 7(j) 8/80 hospital system can\'t be calculated here. Keep 40 and work out 8/80 overtime outside HourTrax. State law has its own exemptions (Pennsylvania: 43 P.S. § 333.105). A federal exception does not excuse state overtime, so name the state basis in the reason too.';

// W-4 Basis (plan 084): mirrors server/services/w4ChangePolicyService.js W4_BASES.
const W4_BASIS_OPTIONS = [
  { value: 'employee_w4', label: "Employee's new W-4" },
  { value: 'irs_lock_in', label: 'IRS lock-in letter' },
  { value: 'data_entry_correction', label: 'Correction (matches the W-4 on file)' },
  { value: 'irs_lock_in_release', label: 'IRS notice releasing the lock-in', lockInOnly: true },
];
// Citations: confirmed by the CPA/HR gate on plan 084 - do not edit without that gate.
const W4_BASIS_TIP = "Every change to federal withholding must rest on a Form W-4 the employee signed, or on an IRS lock-in letter. The employer can't change W-4 values on its own (IRS Pub 15, section 9; Treas. Reg. § 31.3402(f)(2)-1 and § 31.3402(f)(5)-1). Employee's new W-4: enter the date they signed it in W-4 Signed Date. It must be later than the date on file and not after today, and a replacement W-4 must take effect no later than the first payroll period ending on or after the 30th day after you receive it (IRC § 3402(f)(3)(B)); a first W-4 (none on file) takes effect with the first payroll period ending on or after receipt (IRC § 3402(f)(3)(A)). IRS lock-in letter: enter the letter's date in IRS Letter Date and withhold as the letter says. A later W-4 that lowers withholding can't be applied unless the IRS approves it. Correction: you are fixing a typing mistake so the values match the signed W-4 on file; enter that form's date. Keep each signed W-4 and IRS letter for at least 4 years (Treas. Reg. § 31.6001-1(e)(2)). While an IRS lock-in letter is in force, a save that would withhold less than the letter requires is refused and logged; one that withholds more is applied. Save the letter in time for the first payroll that pays wages on or after the start date the letter gives, not before: HourTrax applies the floor from the moment you save it. Record the letter so it applies from the start date printed in the letter, not the day it arrives; the employee has until then to contact the IRS. Give the employee their copy of the letter within 10 business days of receiving it (IRS Pub 15, section 9). An IRS notice that changes the instructions is recorded as another IRS lock-in letter with the notice date; one that lifts them with IRS notice releasing the lock-in and its date. W-4 Received Date is the day you received the signed form: the 30-day rule for a replacement W-4 runs from that date (IRC § 3402(f)(3)(B)). Saves that don't change W-4 values need no basis.";
// Client-side hint only; the server decides (blank = keep, NULL = default).
const W4_FIELDS = [['filing_status', 'status'], ['w4_multiple_jobs', 'bool'], ['w4_dependent_credits', 'money'],
  ['w4_other_income', 'money'], ['w4_deductions', 'money'], ['w4_extra_withholding', 'money'],
  ['federal_allowances', 'money'], ['additional_federal_withholding', 'money']];
const w4Norm = (kind, v) => (kind === 'status' ? (v || 'single') : kind === 'bool' ? (v === true || v === 'true') : Math.round(Number(v || 0) * 100));
const day = (v) => (v ? String(v).slice(0, 10) : '');
const W4_LOCK_IN_TIP = "The IRS lock-in letter states the filing status and withholding instructions you must apply (IRS Pub 15, section 9; Treas. Reg. § 31.3402(f)(2)-1). While it is in force HourTrax refuses any save with Employee's new W-4 or Correction that would withhold less than the letter: a lower-withholding filing status, Step 2 switched off, more Dependent Credits or Deductions, less Other Income or Extra Withholding, or more pre-2020 allowances. A save that withholds the same or more is applied. Save the letter in time for the first payroll that pays wages on or after the start date the letter gives, not before: HourTrax applies the floor from the moment you save it. Record the letter so it applies from the start date printed in the letter, not the day it arrives; the employee has until then to contact the IRS. Give the employee their copy of the letter within 10 business days of receiving it (IRS Pub 15, section 9). If the employee wants less withholding they must ask the IRS; the IRS then sends you a notice. Record a notice that changes the instructions with W-4 Basis IRS lock-in letter and its date; record one that lifts them with IRS notice releasing the lock-in and its date. Keep the letter and notices at least 4 years (Treas. Reg. § 31.6001-1(e)(2)).";
const W4_RECEIVED_TIP = "The day you received the employee's signed Form W-4. A replacement form must take effect no later than the start of the first payroll period ending on or after the 30th day after this date (IRC § 3402(f)(3)(B)); a first W-4 (none on file) takes effect with the first payroll period ending on or after this date (IRC § 3402(f)(3)(A)). It can't be earlier than W-4 Signed Date or later than today. Required for Employee's new W-4; it defaults to today.";
const W4_RELEASE_TIP = "The date printed on the IRS notice that releases the lock-in. On or after the IRS Letter Date and not after today. This save records only the release; enter the employee's new W-4 afterwards in a second save with W-4 Basis Employee's new W-4.";
const STATUS_LABELS = {
  single: 'Single or Married Filing Separately', married_filing_separately: 'Single or Married Filing Separately',
  married_filing_jointly: 'Married Filing Jointly', head_of_household: 'Head of Household',
};
const money = (v) => `$${Number(v || 0).toFixed(2)}`;

export default function PersonFormDialog({
  open, onClose, onSubmit, error,
  editMode, payHidden, payHiddenText = 'Hidden — needs the View pay rates permission', piiHidden, canEditTax, taxOnly, w4Original, canEditLogin, ssnHidden, formData, setFormData, handleInputChange,
  isAdmin, isProgramAdmin, canEditOvertime, canEditMultiplier, currentUser,
  departmentsEnabled, departments, holidaySchedules, tenants, roles,
  generateUsername,
}) {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
  const [showCompensation, setShowCompensation] = useState(false);
  const [showTax, setShowTax] = useState(false);
  const [showAddress, setShowAddress] = useState(false);
  const [showPto, setShowPto] = useState(false);
  const [showPassword, setShowPassword] = useState(false);
  const sz = isMobile ? 'small' : 'medium';
  const psdInvalid = (v) => !!v && !/^\d{6}$/.test(String(v).trim());

  const canManagePayroll = isAdmin || isProgramAdmin;
  const lockIdentity = !!taxOnly;
  const w4Dirty = !!w4Original && (
    W4_FIELDS.some(([k, kind]) => formData[k] !== '' && formData[k] !== undefined && w4Norm(kind, formData[k]) !== w4Norm(kind, w4Original[k]))
    || (!!formData.w4_signed_date && day(formData.w4_signed_date) !== day(w4Original.w4_signed_date))
    || (!!formData.w4_lock_in_letter_date && day(formData.w4_lock_in_letter_date) !== day(w4Original.w4_lock_in_letter_date))
    || (!!formData.w4_received_date && day(formData.w4_received_date) !== day(w4Original.w4_received_date))
    || (!!formData.w4_lock_in_release_date && day(formData.w4_lock_in_release_date) !== day(w4Original.w4_lock_in_release_date)));
  const origSigned = day(w4Original?.w4_signed_date);
  const basisOnFile = W4_BASIS_OPTIONS.find(o => o.value === w4Original?.w4_basis)?.label;
  // Plan 107: the letter is in force while it has a date and no release notice.
  const lockInActive = !!w4Original?.w4_lock_in_letter_date && !w4Original?.w4_lock_in_release_date;
  const lockInReleased = !!w4Original?.w4_lock_in_letter_date && !!w4Original?.w4_lock_in_release_date;
  const basisOptions = W4_BASIS_OPTIONS.filter(o => !o.lockInOnly || lockInActive);
  const irsBasis = formData.w4_basis === 'irs_lock_in' || formData.w4_basis === 'irs_lock_in_release';
  const floor = resolveW4Floor(w4Original); // NULL floor columns read as the current live values, as the server applies them

  const thresholdOver40 = Number(formData.overtime_threshold) > FLSA_WEEKLY_OVERTIME_THRESHOLD;
  // State whose wage-hour rules the warnings use: Work State, else the tax State (Tax / W-4).
  const otStateCode = formData.work_state || formData.state_code || '';
  const otStateName = (US_STATES.find(s => s.code === otStateCode) || {}).label || '';
  const otStateFromTax = !formData.work_state && !!formData.state_code;
  const stateFloor = exemptSalaryFloor(otStateCode);
  const dailyOt = dailyOvertimeRule(otStateCode);
  const stateSourceNote = !otStateCode
    ? ' No Work State or tax State is set, so only federal rules are checked.'
    : (otStateFromTax ? ` (${otStateName} comes from the tax State; set Work State if they work in another state.)` : '');
  const multiplierValue = formData.overtime_multiplier === '' || formData.overtime_multiplier === undefined
    ? null : Number(formData.overtime_multiplier);
  const multiplierBelowFloor = multiplierValue !== null && multiplierValue < FLSA_OVERTIME_MULTIPLIER;
  const multiplierHelper = payHidden ? payHiddenText
    : !canEditMultiplier ? (editMode ? 'Locked — only an Admin can change this' : 'Starts at 1.5× — an Admin can change it with Edit')
    : multiplierBelowFloor ? 'At least 1.5× while overtime is on (FLSA)'
    : (multiplierValue !== null && multiplierValue > FLSA_OVERTIME_MULTIPLIER) ? 'Premium above the FLSA 1.5× — noted in the audit log'
    : undefined;
  // Exempt-status sanity when overtime is off. salary_amount is annual (payroll
  // divides it by 2080), so the weekly figure is salary / 52 whatever the pay frequency.
  const exemptNotice = (() => {
    if (formData.overtime_enabled || formData.employee_type === 'contractor') return null;
    if (formData.employee_type === 'salary') {
      if (payHidden) return null;
      const weekly = Number(formData.salary_amount || 0) / 52;
      const floorText = stateFloor
        ? `${stateFloor.name}'s exempt salary floor of $${stateFloor.weekly.toFixed(2)}/week${stateFloor.note ? ` (${stateFloor.note})` : ''}, ${stateFloor.cite}`
        : '';
      if (weekly < FLSA_EXEMPT_WEEKLY_SALARY) {
        return { severity: 'warning', text: `Salaried employees paid under $${FLSA_EXEMPT_WEEKLY_SALARY}/week ($${FLSA_EXEMPT_ANNUAL_SALARY.toLocaleString()}/year) cannot qualify for the executive, administrative, professional or computer exemption (29 CFR 541.600). This salary is $${weekly.toFixed(2)}/week.${stateFloor ? ` It is also under ${floorText}.` : ''}${stateSourceNote}` };
      }
      if (stateFloor && weekly < stateFloor.weekly) {
        return { severity: 'warning', text: `This salary ($${weekly.toFixed(2)}/week) meets the federal $${FLSA_EXEMPT_WEEKLY_SALARY}/week test but is under ${floorText}. Under ${stateFloor.name} law this person is likely owed state overtime; turn overtime back on unless a state exemption that has no salary test applies.${stateSourceNote}` };
      }
      return { severity: 'info', text: `Salary meets the $${FLSA_EXEMPT_WEEKLY_SALARY}/week federal test${stateFloor ? ` and ${floorText}` : ''}. Exempt status also needs a duties test: executive, administrative, professional, computer or outside sales (29 CFR 541.100–541.500).${stateSourceNote}` };
    }
    return { severity: 'warning', text: `Hourly employees are generally non-exempt under the FLSA and are owed 1.5× pay after 40 hours in a workweek (29 U.S.C. § 207(a)(1)). Exceptions: computer employees whose primary duty meets 29 CFR 541.400 and who are paid at least $${FLSA_COMPUTER_HOURLY}/hour (541.400(b)), outside sales (541.500), and licensed doctors, lawyers and teachers (541.303–541.304). These are federal exceptions. State law may be narrower, so confirm ${otStateName ? otStateName : 'the work state\'s'} treatment before relying on one.${stateSourceNote}` };
  })();
  const hasLoginAccess = formData.loginEnabled;

  // Role field (Gitea #107): only roles the caller may grant, mirrored from the
  // server rules in utils/roleOptions.js; locked on the caller's own login.
  const roleOptions = grantableRoleOptions(currentUser, roles, formData.user_role);
  const roleLocked = editMode && !!formData.user_id && isOwnLogin(currentUser, formData.user_id);
  const roleTip = `${isBuiltinAdmin(currentUser)
    ? 'Built-in roles plus every custom role from Settings > Roles.'
    : 'Employee, Manager and the custom roles from Settings > Roles. Admin and Payroll Admin can be given only by an Admin; a role you can\'t give stays listed only while the person already has it.'
  } Nobody can change their own role. Changing someone's role signs them out everywhere; they get the new permissions when they next sign in.`;
  const roleField = (
    <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: 0.5 }}>
      <TextField fullWidth select label="Role" name="user_role"
        value={formData.user_role} onChange={handleInputChange}
        disabled={roleLocked || !canEditLogin}
        helperText={roleLocked ? 'Nobody can change their own role' : (!canEditLogin ? 'Needs the Manage users permission' : undefined)}
        size={sz}>
        {roleOptions.map(r => (
          <MenuItem key={r.slug} value={r.slug}>{r.name}</MenuItem>
        ))}
      </TextField>
      <Tooltip title={roleTip} arrow>
        <InfoIcon fontSize="small" color="action" sx={{ flexShrink: 0, mt: sz === 'small' ? 1.25 : 2.25 }} />
      </Tooltip>
    </Box>
  );

  const SectionHeader = ({ label, expanded, onToggle }) => (
    <Box
      sx={{ display: 'flex', alignItems: 'center', cursor: 'pointer', py: 1 }}
      onClick={onToggle}
    >
      <Typography variant="subtitle1" sx={{ fontWeight: 600, flex: 1 }}>
        {label}
      </Typography>
      {expanded ? <ExpandLess /> : <ExpandMore />}
    </Box>
  );

  return (
    <Dialog
      open={open}
      onClose={onClose}
      maxWidth="md"
      fullWidth
      fullScreen={isMobile}
      disableEnforceFocus
      aria-labelledby="person-dialog-title"
      onKeyDown={(e) => {
        if (e.key === 'Enter' && (e.ctrlKey || e.metaKey)) {
          e.preventDefault();
          onSubmit();
        }
      }}
    >
      <DialogTitle id="person-dialog-title">
        {editMode ? 'Edit Person' : 'Add New Person'}
      </DialogTitle>
      <DialogContent>
        {error && <Alert severity="error" sx={{ mb: 2, mt: 1 }}>{error}</Alert>}

        {/* Section 1: Identity */}
        <Typography variant="subtitle1" sx={{ fontWeight: 600, mt: 1, mb: 1 }}>
          Identity
        </Typography>
        {taxOnly && (
          <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 1 }}>
            Payroll admins can change only the Tax / W-4 and Address sections here; the other fields are read-only.
          </Typography>
        )}
        <Grid container spacing={2}>
          <Grid item xs={12} sm={6}>
            <TextField fullWidth required label="First Name" name="first_name"
              value={formData.first_name} onChange={handleInputChange} disabled={lockIdentity} size={sz} />
          </Grid>
          <Grid item xs={12} sm={6}>
            <TextField fullWidth required label="Last Name" name="last_name"
              value={formData.last_name} onChange={handleInputChange} disabled={lockIdentity} size={sz} />
          </Grid>
          <Grid item xs={12}>
            <TextField fullWidth required type="email" label="Email" name="email"
              value={formData.email} onChange={handleInputChange} disabled={lockIdentity} size={sz} />
          </Grid>
          <Grid item xs={12} sm={6}>
            {/* Info icon beside the field, never a Tooltip around an input (phone taps). */}
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
              <TextField fullWidth type="date" label="Hire Date" name="hire_date"
                value={formData.hire_date} onChange={handleInputChange}
                required={!editMode} disabled={lockIdentity || (editMode && !canManagePayroll)}
                error={!editMode && !formData.hire_date}
                helperText={!editMode ? 'Required — PTO accrual is prorated from this date'
                  : (!canManagePayroll ? 'Needs the Manage users permission (admin)' : undefined)}
                InputLabelProps={{ shrink: true }} size={sz} />
              <Tooltip title="The first day of employment. PTO accrual for a pay period is counted from this date or the start of the pay period, whichever is later, and when someone is deactivated the current period's share is counted the same way. People added before this was required may have it blank; an admin can fill it in here." arrow>
                <InfoIcon fontSize="small" color="action" sx={{ flexShrink: 0 }} />
              </Tooltip>
            </Box>
          </Grid>
          {departmentsEnabled && departments.length > 0 && (
            <Grid item xs={12} sm={6}>
              <TextField fullWidth select label="Department" name="default_department_id"
                value={formData.default_department_id} onChange={handleInputChange} size={sz} disabled={lockIdentity}
                SelectProps={{ displayEmpty: true }} InputLabelProps={{ shrink: true }}>
                <MenuItem value="">None</MenuItem>
                {departments.map(dept => (
                  <MenuItem key={dept.id} value={dept.id}>{dept.name}</MenuItem>
                ))}
              </TextField>
            </Grid>
          )}
          {editMode && holidaySchedules && holidaySchedules.length > 0 && (
            <Grid item xs={12} sm={6}>
              {/* Tooltip on an info icon, not around the select: wrapped, it opens on
                  focus, sits over the open menu and swallows taps on phones. */}
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                <TextField fullWidth select label="Holiday Schedule" name="holiday_schedule_id"
                  value={formData.holiday_schedule_id} onChange={handleInputChange} size={sz} disabled={lockIdentity}
                  SelectProps={{ displayEmpty: true }} InputLabelProps={{ shrink: true }}>
                  <MenuItem value="">
                    {(() => {
                      const def = holidaySchedules.find(s => s.is_default);
                      return def ? `Tenant default (${def.name})` : 'Tenant default';
                    })()}
                  </MenuItem>
                  {holidaySchedules.map(s => (
                    <MenuItem key={s.id} value={s.id}>{s.name}</MenuItem>
                  ))}
                </TextField>
                <Tooltip title="Which holiday calendar applies to this person. Holidays from the assigned schedule appear on their calendar. Leave on the tenant default unless this person observes a different set of holidays." arrow>
                  <InfoIcon fontSize="small" color="action" sx={{ flexShrink: 0 }} />
                </Tooltip>
              </Box>
            </Grid>
          )}
          {isProgramAdmin && tenants && tenants.length > 0 && (
            <Grid item xs={12} sm={6}>
              <TextField fullWidth select label="Tenant" name="tenant_id"
                value={formData.tenant_id || ''} onChange={handleInputChange} size={sz}>
                {tenants.map(t => (
                  <MenuItem key={t.id} value={t.id}>{t.name || t.display_name}</MenuItem>
                ))}
              </TextField>
            </Grid>
          )}
        </Grid>

        <Divider sx={{ my: 2 }} />

        {/* Section 2: Login Access */}
        <Grid container spacing={2} sx={{ mt: 0 }}>
          <Grid item xs={12}>
            <Typography variant="subtitle1" sx={{ fontWeight: 600 }}>
              Login Access
            </Typography>
          </Grid>
          {/* For new people: always create login, no toggle */}
          {!editMode && (
            <>
              <Grid item xs={12} sm={6}>
                <TextField fullWidth label="Username" name="username"
                  value={formData.username}
                  onChange={handleInputChange}
                  required
                  autoComplete="off"
                  helperText="A temporary password will be auto-generated and shown after creation"
                  size={sz} />
              </Grid>
              <Grid item xs={12} sm={6}>
                {roleField}
              </Grid>
            </>
          )}
          {/* For editing: show login fields if user exists, or toggle to add login */}
          {editMode && formData.user_id && (
            <>
              <Grid item xs={12} sm={6}>
                <TextField fullWidth label="Username" name="username"
                  value={formData.username} disabled size={sz} />
              </Grid>
              <Grid item xs={12} sm={6}>
                {roleField}
              </Grid>
              <Grid item xs={12} sm={6}>
                <FormControlLabel
                  control={
                    <Switch
                      disabled={!canEditLogin}
                      checked={formData.user_is_active !== false}
                      onChange={(e) => setFormData(prev => ({ ...prev, user_is_active: e.target.checked }))}
                    />
                  }
                  label="Account Active"
                />
              </Grid>
            </>
          )}
          {/* For editing employee-only: show toggle to add login (legacy support) */}
          {editMode && !formData.user_id && canEditLogin && (
            <>
              <Grid item xs={12}>
                <FormControlLabel
                  control={
                    <Switch
                      checked={hasLoginAccess}
                      onChange={(e) => {
                        const enabled = e.target.checked;
                        setFormData(prev => {
                          const updated = { ...prev, loginEnabled: enabled };
                          if (enabled) {
                            updated.addingLogin = true;
                            updated.username = prev.email || (generateUsername ? generateUsername(prev.first_name, prev.last_name) : '');
                            updated.user_role = prev.user_role || 'user';
                          } else {
                            updated.addingLogin = false;
                          }
                          return updated;
                        });
                      }}
                    />
                  }
                  label={hasLoginAccess ? 'Adding login access' : 'No login — toggle to add'}
                />
              </Grid>
              {hasLoginAccess && (
                <>
                  <Grid item xs={12} sm={6}>
                    <TextField fullWidth label="Username" name="username"
                      value={formData.username}
                      onChange={handleInputChange}
                      required autoComplete="off" size={sz} />
                  </Grid>
                  <Grid item xs={12} sm={6}>
                    {roleField}
                  </Grid>
                  <Grid item xs={12} sm={6}>
                    <TextField
                      fullWidth label="Password" name="password"
                      type={showPassword ? 'text' : 'password'}
                      value={formData.password || ''}
                      onChange={handleInputChange}
                      required size={sz}
                      InputProps={{
                        endAdornment: (
                          <InputAdornment position="end">
                            <IconButton onClick={() => setShowPassword(!showPassword)} edge="end">
                              {showPassword ? <VisibilityOff /> : <Visibility />}
                            </IconButton>
                          </InputAdornment>
                        ),
                      }}
                    />
                  </Grid>
                  {formData.password && (
                    <Grid item xs={12}>
                      <PasswordStrengthIndicator password={formData.password} compact />
                    </Grid>
                  )}
                </>
              )}
            </>
          )}
        </Grid>

        {/* Section 3: Compensation — admin only */}
        {canManagePayroll && (
          <>
            <Divider sx={{ my: 2 }} />
            <SectionHeader
              label="Compensation"
              expanded={showCompensation}
              onToggle={() => setShowCompensation(!showCompensation)}
            />
            <Collapse in={showCompensation}>
              <Grid container spacing={2}>
                <Grid item xs={12} sm={6}>
                  <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: 0.5 }}>
                    <TextField fullWidth select label="Employee Type" name="employee_type"
                      value={formData.employee_type} onChange={handleInputChange} size={sz}
                      helperText={formData.employee_type === 'contractor'
                        ? 'Paid outside payroll — no tax withholding, no pay stub. Hourly Rate is still used to total their hours.'
                        : undefined}>
                      <MenuItem value="hourly">Hourly</MenuItem>
                      <MenuItem value="salary">Salary</MenuItem>
                      <MenuItem value="contractor">Contractor (1099)</MenuItem>
                    </TextField>
                    <Tooltip title="Hourly and Salary run through payroll with tax withholding. Contractor (1099) workers are paid outside HourTrax payroll — payroll runs skip them, and they don't see Time Off, My Pay or My Benefits. Their hours are still totaled at the Hourly Rate." arrow>
                      <InfoIcon fontSize="small" color="action" sx={{ flexShrink: 0, mt: sz === 'small' ? 1.25 : 2.25 }} />
                    </Tooltip>
                  </Box>
                </Grid>
                <Grid item xs={12} sm={6}>
                  <TextField fullWidth type="number" label="Hourly Rate" name="hourly_rate"
                    value={formData.hourly_rate} onChange={handleInputChange}
                    disabled={!!payHidden} helperText={payHidden ? payHiddenText : undefined}
                    InputProps={{ startAdornment: '$' }}
                    inputProps={{ step: '0.01', min: '0' }} size={sz} />
                </Grid>
                {formData.employee_type === 'salary' && (
                  <Grid item xs={12} sm={6}>
                    <TextField fullWidth type="number" label="Annual Salary" name="salary_amount"
                      value={formData.salary_amount} onChange={handleInputChange}
                      disabled={!!payHidden} helperText={payHidden ? payHiddenText : undefined}
                      InputProps={{ startAdornment: '$' }}
                      inputProps={{ step: '100', min: '0' }} size={sz} />
                  </Grid>
                )}
                <Grid item xs={12} sm={6}>
                  <TextField fullWidth select label="Pay Frequency" name="pay_frequency"
                    value={formData.pay_frequency} onChange={handleInputChange} size={sz}>
                    <MenuItem value="weekly">Weekly</MenuItem>
                    <MenuItem value="biweekly">Biweekly</MenuItem>
                    <MenuItem value="semimonthly">Semi-Monthly</MenuItem>
                    <MenuItem value="monthly">Monthly</MenuItem>
                  </TextField>
                </Grid>
              </Grid>
            </Collapse>
          </>
        )}

        {/* Overtime: independent of the compensation gate; editing is governed by canEditOvertime */}
        <Divider sx={{ my: 2 }} />
        <Typography variant="subtitle1" sx={{ fontWeight: 600, py: 1 }}>Overtime</Typography>
        <Grid container spacing={2}>
                <Grid item xs={12}>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                    <FormControlLabel
                      control={
                        <Switch checked={formData.overtime_enabled} disabled={!canEditOvertime}
                          onChange={(e) => setFormData(prev => ({ ...prev, overtime_enabled: e.target.checked }))} />
                      }
                      label="Enable Overtime Calculation"
                    />
                    <Tooltip title={OVERTIME_SWITCH_TIP} arrow>
                      <InfoIcon fontSize="small" color="action" />
                    </Tooltip>
                  </Box>
                  {!canEditOvertime && (
                    <Typography variant="caption" color="text.secondary">
                      Locked — needs the Manage employees or Run payroll permission
                    </Typography>
                  )}
                </Grid>
                <Grid item xs={12} sm={6}>
                  <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: 0.5 }}>
                    <TextField fullWidth select label="Work State" name="work_state"
                      value={formData.work_state || ''} onChange={handleInputChange}
                      disabled={!canEditOvertime || !!taxOnly} size="small" InputLabelProps={{ shrink: true }}
                      SelectProps={{ displayEmpty: true }}
                      helperText={taxOnly ? 'Needs the Manage employees permission'
                        : !formData.work_state
                        ? (formData.state_code ? `Using the tax State (${formData.state_code})` : 'Not set — only federal rules are checked')
                        : undefined}>
                      <MenuItem value="">Same as tax State</MenuItem>
                      {US_STATES.filter(s => s.code).map(s => (
                        <MenuItem key={s.code} value={s.code}>{`${s.code} — ${s.label}`}</MenuItem>
                      ))}
                    </TextField>
                    <Tooltip title={WORK_STATE_TIP} arrow>
                      <InfoIcon fontSize="small" color="action" sx={{ flexShrink: 0, mt: 1.25 }} />
                    </Tooltip>
                  </Box>
                </Grid>
                {!formData.overtime_enabled && editMode && multiplierBelowFloor && !payHidden && (
                  <Grid item xs={12} sm={6}>
                    <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: 0.5 }}>
                      <TextField fullWidth type="number" label="OT Multiplier"
                        name="overtime_multiplier" value={formData.overtime_multiplier}
                        onChange={handleInputChange} disabled={!canEditMultiplier}
                        error={canEditMultiplier} helperText={multiplierHelper}
                        inputProps={{ step: '0.1', min: '1.5', max: '3' }} size={sz} />
                      <Tooltip title={OVERTIME_MULTIPLIER_TIP} arrow>
                        <InfoIcon fontSize="small" color="action" sx={{ flexShrink: 0, mt: sz === 'small' ? 1.25 : 2.25 }} />
                      </Tooltip>
                    </Box>
                  </Grid>
                )}
                {formData.overtime_enabled && (
                  <>
                    {dailyOt && formData.employee_type !== 'contractor' && (
                      <Grid item xs={12}>
                        <Alert severity="warning">
                          {`Daily overtime applies in ${dailyOt.name}: ${dailyOt.rule} (${dailyOt.cite}). HourTrax computes weekly overtime only, so daily overtime has to be worked out and paid outside HourTrax.`}
                          {otStateFromTax ? ` (${otStateName} comes from the tax State; set Work State if they work in another state.)` : ''}
                        </Alert>
                      </Grid>
                    )}
                    <Grid item xs={12} sm={6}>
                      <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: 0.5 }}>
                        <TextField fullWidth type="number" label="OT Threshold (hrs/week)"
                          name="overtime_threshold" value={formData.overtime_threshold}
                          onChange={handleInputChange} disabled={!canEditOvertime}
                          helperText={thresholdOver40 ? 'Above the FLSA 40-hour week — pick an FLSA Exception and give an Exception Reason' : undefined}
                          inputProps={{ step: '1', min: '1', max: '168' }} size={sz} />
                        <Tooltip title={OVERTIME_THRESHOLD_TIP} arrow>
                          <InfoIcon fontSize="small" color="action" sx={{ flexShrink: 0, mt: sz === 'small' ? 1.25 : 2.25 }} />
                        </Tooltip>
                      </Box>
                    </Grid>
                    <Grid item xs={12} sm={6}>
                      <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: 0.5 }}>
                        <TextField fullWidth type="number" label="OT Multiplier"
                          name="overtime_multiplier" value={formData.overtime_multiplier}
                          onChange={handleInputChange} disabled={!canEditMultiplier || !!payHidden}
                          error={canEditMultiplier && multiplierBelowFloor}
                          helperText={multiplierHelper}
                          inputProps={{ step: '0.1', min: '1.5', max: '3' }} size={sz} />
                        <Tooltip title={OVERTIME_MULTIPLIER_TIP} arrow>
                          <InfoIcon fontSize="small" color="action" sx={{ flexShrink: 0, mt: sz === 'small' ? 1.25 : 2.25 }} />
                        </Tooltip>
                      </Box>
                    </Grid>
                    {thresholdOver40 && (
                      <>
                        <Grid item xs={12} sm={6}>
                          <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: 0.5 }}>
                            <TextField fullWidth select label="FLSA Exception" name="overtime_exception"
                              value={formData.overtime_exception || ''} onChange={handleInputChange}
                              disabled={!canEditOvertime} required size={sz} InputLabelProps={{ shrink: true }}
                              SelectProps={{ displayEmpty: true }} helperText="Required for a threshold above 40">
                              <MenuItem value="">Select…</MenuItem>
                              {OVERTIME_EXCEPTIONS.map(o => (
                                <MenuItem key={o.code} value={o.code}>{o.label}</MenuItem>
                              ))}
                            </TextField>
                            <Tooltip title={OVERTIME_EXCEPTION_TIP} arrow>
                              <InfoIcon fontSize="small" color="action" sx={{ flexShrink: 0, mt: sz === 'small' ? 1.25 : 2.25 }} />
                            </Tooltip>
                          </Box>
                        </Grid>
                        <Grid item xs={12} sm={6}>
                          <TextField fullWidth label="Exception Reason" name="overtime_exception_reason"
                            value={formData.overtime_exception_reason || ''} onChange={handleInputChange}
                            disabled={!canEditOvertime} required size={sz} inputProps={{ maxLength: 500 }}
                            helperText={formData.overtime_exception === '7k_fire_police'
                              ? '§ 7(k): give the work period\'s length (7 to 28 days) and the date and time it starts (29 CFR § 553.224 for the 7 to 28 day period; § 553.51 for the record), plus the agency. Saved on this person and written to the audit log.'
                              : 'Name the facility, agreement or statute subsection. Saved on this person and written to the audit log.'} />
                        </Grid>
                      </>
                    )}
                  </>
                )}
                {!formData.overtime_enabled && formData.employee_type !== 'contractor' && (
                  <>
                    {exemptNotice && (
                      <Grid item xs={12}>
                        <Alert severity={exemptNotice.severity}>{exemptNotice.text}</Alert>
                      </Grid>
                    )}
                    <Grid item xs={12}>
                      <TextField fullWidth label="Reason overtime is off" name="overtime_disabled_basis"
                        value={formData.overtime_disabled_basis || ''} onChange={handleInputChange}
                        disabled={!canEditOvertime} size={sz} inputProps={{ maxLength: 500 }}
                        helperText="Optional. Written to the audit log with this save only; it is not stored on the person and will be blank next time." />
                    </Grid>
                  </>
                )}
        </Grid>

        {/* Section 4: Tax / W-4 — admins and payroll admins, edit only, hidden when the server withheld W-4 data */}
        {canEditTax && editMode && !piiHidden && (
          <>
            <Divider sx={{ my: 2 }} />
            <SectionHeader
              label="Tax / W-4"
              expanded={showTax}
              onToggle={() => setShowTax(!showTax)}
            />
            <Collapse in={showTax}>
              <Grid container spacing={2}>
                {lockInActive && (
                  <Grid item xs={12}>
                    <Alert severity="warning"
                      action={<Tooltip title={W4_LOCK_IN_TIP} arrow><InfoIcon fontSize="small" color="action" sx={{ mt: 0.5 }} /></Tooltip>}>
                      An IRS lock-in letter dated {day(w4Original.w4_lock_in_letter_date)} is in force. It requires at least:
                      Filing Status {STATUS_LABELS[floor.filing_status] || STATUS_LABELS.single};
                      Step 2: Multiple Jobs {floor.multiple_jobs ? 'on' : 'off'};
                      Dependent Credits at most {money(floor.dependent_credits)};
                      Other Income at least {money(floor.other_income)};
                      Deductions at most {money(floor.deductions)};
                      Extra Withholding at least {money(floor.extra)} per pay period
                      {floor.allowances > 0 ? `; Federal Allowances at most ${floor.allowances}` : ''}.
                      {floor.incomplete && ' Floor not yet recorded for this letter — current values apply until it is re-saved as an IRS lock-in letter.'}
                      A save that would withhold less is refused until an IRS notice modifies or releases the lock-in.
                    </Alert>
                  </Grid>
                )}
                {lockInReleased && (
                  <Grid item xs={12}>
                    <Alert severity="info">
                      The IRS lock-in letter dated {day(w4Original.w4_lock_in_letter_date)} was released by an IRS notice dated {day(w4Original.w4_lock_in_release_date)}. The employee's own W-4 applies again.
                    </Alert>
                  </Grid>
                )}
                <Grid item xs={12} sm={6}>
                  <TextField fullWidth select label="Filing Status" name="filing_status"
                    value={formData.filing_status} onChange={handleInputChange} size={sz}>
                    <MenuItem value="single">Single or Married Filing Separately</MenuItem>
                    <MenuItem value="married_filing_jointly">Married Filing Jointly</MenuItem>
                    <MenuItem value="head_of_household">Head of Household</MenuItem>
                  </TextField>
                </Grid>
                <Grid item xs={12} sm={6}>
                  <FormControlLabel
                    control={
                      <Switch checked={formData.w4_multiple_jobs || false}
                        onChange={(e) => setFormData(prev => ({ ...prev, w4_multiple_jobs: e.target.checked }))} />
                    }
                    label="Step 2: Multiple Jobs"
                    sx={{ mt: 1 }}
                  />
                </Grid>
                <Grid item xs={12} sm={6}>
                  <TextField fullWidth type="number" label="Dependent Credits"
                    name="w4_dependent_credits" value={formData.w4_dependent_credits}
                    onChange={handleInputChange} InputProps={{ startAdornment: '$' }}
                    inputProps={{ min: 0, step: '500' }} size={sz} />
                </Grid>
                <Grid item xs={12} sm={6}>
                  <TextField fullWidth type="number" label="Other Income"
                    name="w4_other_income" value={formData.w4_other_income}
                    onChange={handleInputChange} InputProps={{ startAdornment: '$' }}
                    inputProps={{ min: 0, step: '100' }} size={sz} />
                </Grid>
                <Grid item xs={12} sm={6}>
                  <TextField fullWidth type="number" label="Deductions"
                    name="w4_deductions" value={formData.w4_deductions}
                    onChange={handleInputChange} InputProps={{ startAdornment: '$' }}
                    inputProps={{ min: 0, step: '100' }} size={sz} />
                </Grid>
                <Grid item xs={12} sm={6}>
                  <TextField fullWidth type="number" label="Extra Withholding"
                    name="w4_extra_withholding" value={formData.w4_extra_withholding}
                    onChange={handleInputChange} InputProps={{ startAdornment: '$' }}
                    inputProps={{ min: 0, step: '1' }} size={sz} />
                </Grid>
                <Grid item xs={12} sm={6}>
                  {/* Info icon beside the select, never a Tooltip around it (phone taps). */}
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                    <TextField fullWidth select label="W-4 Basis" name="w4_basis"
                      value={formData.w4_basis || ''} onChange={handleInputChange}
                      required={w4Dirty} error={w4Dirty && !formData.w4_basis}
                      helperText={w4Dirty && !formData.w4_basis ? 'Required: you changed a W-4 field'
                        : (basisOnFile ? `On file: ${basisOnFile}` : 'Choose only when W-4 values or the signed date change')}
                      SelectProps={{ displayEmpty: true }} InputLabelProps={{ shrink: true }} size="small">
                      <MenuItem value="">Not changing the W-4</MenuItem>
                      {basisOptions.map(o => <MenuItem key={o.value} value={o.value}>{o.label}</MenuItem>)}
                    </TextField>
                    <Tooltip title={W4_BASIS_TIP} arrow>
                      <InfoIcon fontSize="small" color="action" sx={{ flexShrink: 0 }} />
                    </Tooltip>
                  </Box>
                </Grid>
                <Grid item xs={12} sm={6}>
                  <TextField fullWidth type="date" label="W-4 Signed Date" name="w4_signed_date"
                    value={formData.w4_signed_date} onChange={handleInputChange}
                    disabled={irsBasis}
                    helperText={formData.w4_basis === 'employee_w4'
                      ? `Date the employee signed the new W-4${origSigned ? ` (after ${origSigned})` : ''}`
                      : (irsBasis ? "Stays as it is: it is the employee's own signature date" : undefined)}
                    InputLabelProps={{ shrink: true }} size="small" />
                </Grid>
                {(formData.w4_basis === 'employee_w4' || formData.w4_basis === 'data_entry_correction' || !!w4Original?.w4_received_date) && (
                  <Grid item xs={12} sm={6}>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                      <TextField fullWidth type="date" label="W-4 Received Date" name="w4_received_date"
                        value={formData.w4_received_date || ''} onChange={handleInputChange}
                        required={formData.w4_basis === 'employee_w4'} disabled={irsBasis}
                        helperText={formData.w4_basis === 'employee_w4' ? 'The day you received the signed form' : undefined}
                        InputLabelProps={{ shrink: true }} size="small" />
                      <Tooltip title={W4_RECEIVED_TIP} arrow>
                        <InfoIcon fontSize="small" color="action" sx={{ flexShrink: 0 }} />
                      </Tooltip>
                    </Box>
                  </Grid>
                )}
                {(formData.w4_basis === 'irs_lock_in' || !!w4Original?.w4_lock_in_letter_date) && (
                  <Grid item xs={12} sm={6}>
                    <TextField fullWidth required={formData.w4_basis === 'irs_lock_in'} disabled={formData.w4_basis === 'employee_w4' || formData.w4_basis === 'irs_lock_in_release'} type="date" label="IRS Letter Date" name="w4_lock_in_letter_date"
                      value={formData.w4_lock_in_letter_date || ''} onChange={handleInputChange}
                      helperText="The date printed on the IRS lock-in letter"
                      InputLabelProps={{ shrink: true }} size="small" />
                  </Grid>
                )}
                {formData.w4_basis === 'irs_lock_in_release' && (
                  <Grid item xs={12} sm={6}>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                      <TextField fullWidth required type="date" label="IRS Release Notice Date" name="w4_lock_in_release_date"
                        value={formData.w4_lock_in_release_date || ''} onChange={handleInputChange}
                        helperText={`The date printed on the IRS notice (on or after ${day(w4Original?.w4_lock_in_letter_date)})`}
                        InputLabelProps={{ shrink: true }} size="small" />
                      <Tooltip title={W4_RELEASE_TIP} arrow>
                        <InfoIcon fontSize="small" color="action" sx={{ flexShrink: 0 }} />
                      </Tooltip>
                    </Box>
                  </Grid>
                )}
                <Grid item xs={12} sm={6}>
                  <TextField fullWidth label="SSN (Last 4)" name="ssn_last_four"
                    value={formData.ssn_last_four} onChange={handleInputChange}
                    disabled={!!ssnHidden}
                    helperText={ssnHidden ? 'Hidden — needs the Manage users permission' : undefined}
                    inputProps={{ maxLength: 4, pattern: '[0-9]*' }} size={sz} />
                </Grid>
                <Grid item xs={12} sm={6}>
                  <TextField fullWidth type="date" label="Date of Birth" name="date_of_birth"
                    value={formData.date_of_birth} onChange={handleInputChange}
                    InputLabelProps={{ shrink: true }} size={sz} />
                </Grid>

                {/* State Tax */}
                <Grid item xs={12}>
                  <Typography variant="subtitle2" color="text.secondary" sx={{ mt: 1 }}>
                    State Tax
                  </Typography>
                </Grid>
                <Grid item xs={12} sm={4}>
                  <TextField fullWidth select label="State" name="state_code"
                    value={formData.state_code} onChange={handleInputChange} size={sz}>
                    {US_STATES.map(s => (
                      <MenuItem key={s.code} value={s.code}>
                        {s.code ? `${s.code} — ${s.label}` : s.label}
                      </MenuItem>
                    ))}
                  </TextField>
                </Grid>
                <Grid item xs={12} sm={4}>
                  <TextField fullWidth type="number" label="State Allowances"
                    name="state_allowances" value={formData.state_allowances}
                    onChange={handleInputChange} inputProps={{ min: 0, step: 1 }} size={sz} />
                </Grid>
                <Grid item xs={12} sm={4}>
                  <TextField fullWidth type="number" label="Addl State Withholding"
                    name="additional_state_withholding" value={formData.additional_state_withholding}
                    onChange={handleInputChange} InputProps={{ startAdornment: '$' }}
                    inputProps={{ min: 0, step: '0.01' }} size={sz} />
                </Grid>

                {/* Local Tax */}
                <Grid item xs={12}>
                  <Typography variant="subtitle2" color="text.secondary" sx={{ mt: 1 }}>
                    Local Tax (PA Municipalities)
                  </Typography>
                </Grid>
                <Grid item xs={12} sm={6}>
                  <TextField fullWidth label="Tax Jurisdiction" name="local_tax_jurisdiction"
                    value={formData.local_tax_jurisdiction || ''} onChange={handleInputChange}
                    placeholder="e.g. Millcreek Township" size={sz}
                    helperText="Municipality name for local taxes" />
                </Grid>
                <Grid item xs={12} sm={6}>
                  <TextField fullWidth type="number" label="Local EIT Rate"
                    name="local_eit_rate" value={formData.local_eit_rate || ''}
                    onChange={handleInputChange}
                    inputProps={{ min: 0, step: '0.0001' }} size={sz}
                    helperText="e.g. 0.01 = 1%" />
                </Grid>
                <Grid item xs={12} sm={6}>
                  <TextField fullWidth type="number" label="Local LST (Annual)"
                    name="local_lst_annual" value={formData.local_lst_annual || ''}
                    onChange={handleInputChange} InputProps={{ startAdornment: '$' }}
                    inputProps={{ min: 0, step: '0.01' }} size={sz}
                    helperText="Annual amount, divided by pay periods" />
                </Grid>
                <Grid item xs={12} sm={6}>
                  <TextField fullWidth type="number" label="PA UC Employee Rate"
                    name="pa_uc_employee_rate" value={formData.pa_uc_employee_rate || ''}
                    onChange={handleInputChange}
                    inputProps={{ min: 0, step: '0.0001' }} size={sz}
                    helperText="e.g. 0.0006 = 0.06%" />
                </Grid>
                <Grid item xs={12} sm={6}>
                  <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: 0.5 }}>
                    <TextField fullWidth label="Resident PSD Code" name="resident_psd_code"
                      value={formData.resident_psd_code || ''} onChange={handleInputChange}
                      error={psdInvalid(formData.resident_psd_code)}
                      helperText={psdInvalid(formData.resident_psd_code) ? 'Must be exactly 6 digits' : 'Where this person lives'}
                      inputProps={{ maxLength: 6, inputMode: 'numeric' }} size={sz} />
                    <Tooltip title={RESIDENT_PSD_TIP} arrow>
                      <InfoIcon fontSize="small" color="action" sx={{ flexShrink: 0, mt: sz === 'small' ? 1.25 : 2.25 }} />
                    </Tooltip>
                  </Box>
                </Grid>
                <Grid item xs={12} sm={6}>
                  <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: 0.5 }}>
                    <TextField fullWidth label="Work Location PSD Code" name="work_psd_code"
                      value={formData.work_psd_code || ''} onChange={handleInputChange}
                      error={psdInvalid(formData.work_psd_code)}
                      helperText={psdInvalid(formData.work_psd_code) ? 'Must be exactly 6 digits' : 'Where this person works (home code if they work from home)'}
                      inputProps={{ maxLength: 6, inputMode: 'numeric' }} size={sz} />
                    <Tooltip title={WORK_PSD_TIP} arrow>
                      <InfoIcon fontSize="small" color="action" sx={{ flexShrink: 0, mt: sz === 'small' ? 1.25 : 2.25 }} />
                    </Tooltip>
                  </Box>
                </Grid>
                <Grid item xs={12} sm={6}>
                  <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: 0.5 }}>
                    <TextField fullWidth label="EIT/LST Collector" name="local_tax_collector"
                      value={formData.local_tax_collector || ''} onChange={handleInputChange}
                      helperText="e.g. Berkheimer Tax Administrator"
                      inputProps={{ maxLength: 100 }} size={sz} />
                    <Tooltip title={LOCAL_COLLECTOR_TIP} arrow>
                      <InfoIcon fontSize="small" color="action" sx={{ flexShrink: 0, mt: sz === 'small' ? 1.25 : 2.25 }} />
                    </Tooltip>
                  </Box>
                </Grid>
              </Grid>
            </Collapse>
          </>
        )}

        {/* Section 5: Address — admins and payroll admins, edit only, hidden when the server withheld the address */}
        {canEditTax && editMode && !piiHidden && (
          <>
            <Divider sx={{ my: 2 }} />
            <SectionHeader
              label="Address"
              expanded={showAddress}
              onToggle={() => setShowAddress(!showAddress)}
            />
            <Collapse in={showAddress}>
              <Grid container spacing={2}>
                <Grid item xs={12}>
                  <TextField fullWidth label="Street Address" name="street_address"
                    value={formData.street_address || ''} onChange={handleInputChange}
                    placeholder="123 Main St, Apt 4" size={sz} />
                </Grid>
                <Grid item xs={12} sm={5}>
                  <TextField fullWidth label="City" name="city"
                    value={formData.city || ''} onChange={handleInputChange} size={sz} />
                </Grid>
                <Grid item xs={6} sm={3}>
                  <TextField fullWidth label="State" value={formData.state_code || ''}
                    disabled size={sz} />
                </Grid>
                <Grid item xs={6} sm={4}>
                  <TextField fullWidth label="ZIP Code" name="zip_code"
                    value={formData.zip_code || ''} onChange={handleInputChange}
                    inputProps={{ maxLength: 10 }} size={sz} />
                </Grid>
              </Grid>
            </Collapse>
          </>
        )}

        {/* Section 6: PTO — admin only, edit only */}
        {canManagePayroll && editMode && (
          <>
            <Divider sx={{ my: 2 }} />
            <SectionHeader
              label="PTO / Time Off"
              expanded={showPto}
              onToggle={() => setShowPto(!showPto)}
            />
            <Collapse in={showPto}>
              <Grid container spacing={2}>
                <Grid item xs={12} sm={4}>
                  <TextField fullWidth type="number" label="Accrual Rate (hrs)"
                    name="pto_accrual_rate" value={formData.pto_accrual_rate || 0}
                    onChange={handleInputChange} inputProps={{ min: 0, step: '0.25' }}
                    helperText="Hours earned per period" size={sz} />
                </Grid>
                <Grid item xs={12} sm={4}>
                  <TextField fullWidth select label="Accrual Frequency"
                    name="pto_accrual_frequency"
                    value={formData.pto_accrual_frequency || 'per_pay_period'}
                    onChange={handleInputChange} size={sz}>
                    <MenuItem value="per_pay_period">Per Pay Period</MenuItem>
                    <MenuItem value="monthly">Monthly</MenuItem>
                    <MenuItem value="annually">Annually</MenuItem>
                  </TextField>
                </Grid>
                <Grid item xs={12} sm={4}>
                  <TextField fullWidth type="number" label="Max Balance (hrs)"
                    name="pto_max_balance" value={formData.pto_max_balance || 0}
                    onChange={handleInputChange} inputProps={{ min: 0, step: '1' }}
                    helperText="0 = unlimited" size={sz} />
                </Grid>
                <Grid item xs={12} sm={6}>
                  <TextField fullWidth type="number" label="Current PTO Balance (hrs)"
                    name="pto_balance" value={formData.pto_balance || 0}
                    onChange={handleInputChange} inputProps={{ min: 0, step: '0.25' }} size={sz} />
                </Grid>
                <Grid item xs={12} sm={6}>
                  <TextField fullWidth type="number" label="Carryover Limit (hrs)"
                    name="pto_carryover_limit" value={formData.pto_carryover_limit || 0}
                    onChange={handleInputChange} inputProps={{ min: 0, step: '1' }}
                    helperText="0 = unlimited" size={sz} />
                </Grid>
              </Grid>
            </Collapse>
          </>
        )}
      </DialogContent>
      <DialogActions sx={{ p: isMobile ? 2 : 1 }}>
        <Button onClick={onClose} size={isMobile ? 'large' : 'medium'}>Cancel</Button>
        <Button variant="contained" onClick={onSubmit} size={isMobile ? 'large' : 'medium'}>
          {editMode ? 'Update' : 'Create'}
        </Button>
      </DialogActions>
    </Dialog>
  );
}
